import { Badge, BlockStack, Box, Button, Card, IndexTable, InlineStack, Modal, Page, Pagination, Tabs, Text } from '@shopify/polaris';
import { DeleteMajor } from '@shopify/polaris-icons';
import React, { useEffect, useRef, useState } from 'react';
import { FileIcon } from '../../../Asests/svgIcon/icon';
import { DI, DIProps } from '../../../Core';
import { TabsI, StaffI, ModalI } from "./interface"
import { getStatusBadge, tabs as initTabs } from "./helper"
import { useTranslation } from 'react-i18next';

const limit = 10;

const StaffUserManagement = (props: DIProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(0);
  const [tabs, setTabs] = useState<any>(initTabs(t))
  const [staffs, setStaffs] = useState<any>([]);
  const [statusCount, setStatusCount] = useState(0);
  const [activePage, setActivePage] = useState(1);

  const [getLoader, setgetLoader] = useState(false);

  const [modal, setModal] = useState<ModalI>({ open: false, executeAPI: "", id: "" })

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex)
  };

  const getStaffs = () => {
    setgetLoader(true);
    props.di.GET("staff/getStaffs", {
      page: activePage,
      limit: limit,
      "filter[status]": tabs[selected].id
    }).then((res) => {
      if (res?.success) {
        setStaffs(res?.data?.map((staff: any) => {
          if (staff?.type === "owner") return;
          return {
            name: staff?.name ?? "",
            email: staff?.email ?? "",
            status: staff?.status ?? "",
            created_at: staff?.created_at ?? "",
            approved_at: staff?.approved_at ?? "",
            rejected_at: staff?.rejected_at ?? "",
            id: staff?.staff_user_id ?? "",
            role_id: staff?.staff_role_id ?? ""
          }
        })?.filter((a: any) => a) ?? [])
        setStatusCount(res?.total ?? 0);
      } else {
        setStaffs([]);
        setStatusCount(0)
      }
    }).finally(() => {
      setgetLoader(false);
    })
  }

  const staffAction = () => {
    setModal((prev) => ({ ...prev, loading: true }));
    props.di.POST(modal.executeAPI, {
      staff_user_id: modal.id,
      templateSource: "amazon"
    }).then((res) => {
      if (res?.success) {
        props.success(res?.message ?? `Staff ${modal?.key ?? "action performed"} successfully!`);
        getStaffs();
      } else {
        props.error(res?.message ?? "Something went wrong!");
      }
    }).finally(() => {
      setModal((prev) => ({ ...prev, open: false, loading: false, id: "", executeAPI: "" }));
    })
  }

  useEffect(() => {
    getStaffs();
  }, [selected, activePage])

  const rowMarkup = staffs?.map(({ id, name, email, status, role_id, ...staff }: any, index: number) => {
    const { tone, updatedStatus } = getStatusBadge(status, t)
    return <IndexTable.Row id={id} key={id} position={index}>
      <IndexTable.Cell>
        <InlineStack gap={'200'}>
          <Text variant="bodyMd" fontWeight="bold" as="span">
            {name}
          </Text>
          {/* {isNew && <Badge tone="info">New</Badge>} */}
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>{email}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={tone} size="medium">{updatedStatus}</Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" numeric>
          {staff[tabs[selected].date_key]}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap={'200'}>
          {tabs[selected].show_approve && <Button variant="primary" size="micro" onClick={() => {
            setModal({
              title: t("Approve Staff"),
              description: <Text as="p">{t("Are you sure you want to approve staff:")} <Text as="strong">{name}</Text>?</Text>,
              key: "approved",
              id,
              open: true,
              primaryActionContent: t("Approve"),
              executeAPI: "staff/approve",
            })
          }}>
            {t("Approve")}
          </Button>}
          {tabs[selected].show_rejected && <Button
            variant="secondary"
            tone="critical"
            size="micro"
            onClick={() => {
              setModal({
                title: t("Reject Staff"),
                description: <Text as="p">{t("Are you sure you want to reject staff:")} <Text as="strong">{name}</Text>?</Text>,
                destructive: true,
                key: "rejected",
                id,
                open: true,
                primaryActionContent: t("Reject"),
                executeAPI: "staff/reject"
              });
            }}
          >
            {t("Reject")}
          </Button>}
          <Button
            icon={DeleteMajor}
            size="micro"
            variant="monochromePlain"
            onClick={() => {
              setModal({
                title: t("Delete Staff"),
                description: <Text as="p">{t("Are you sure you want to delete staff:")} <Text as="strong">{name}</Text>?</Text>,
                destructive: true,
                key: "deleted",
                id,
                open: true,
                primaryActionContent: t("Delete"),
                executeAPI: "staff/delete"
              });
            }}
          />
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  });

  return (
    <React.Fragment>
      {/* <Card padding={'300'}> */}
      <BlockStack gap={'300'}>
        <Tabs
          tabs={tabs}
          selected={selected}
          onSelect={handleTabChange}
        />
        <Box paddingInlineStart="300" paddingBlockEnd="300" paddingInlineEnd="300">
          <Card padding="0" background="bg-fill-secondary">
            <IndexTable
              itemCount={staffs.length}
              selectable={false}
              loading={getLoader}
              emptyState={
                <Box padding={"400"}>
                  <InlineStack gap={'100'} align="center">
                    <FileIcon />
                    <Text as="p" tone="subdued">
                      {t(`No ${tabs[selected]?.panelID.toLowerCase()} staff ${selected < 2 ? "management requests" : ""}.`)}
                    </Text>
                  </InlineStack>
                </Box>
              }
              headings={[
                { title: t('Staff Name') },
                { title: t('Email Id') },
                { title: t('Status') },
                { title: `${t(tabs[selected].date)} ${t("date")}` },
                { title: t('Action') },
              ]}>
              {rowMarkup}
            </IndexTable>
          </Card>
        </Box>
        {statusCount > 0 && statusCount / limit > 1 && <InlineStack align='center'>
          <Pagination
            label={activePage}
            hasNext={activePage < statusCount / limit}
            hasPrevious={activePage > 1}
            onNext={() => {
              setActivePage(prev => prev + 1)
            }}
            onPrevious={() => {
              setActivePage(prev => prev - 1)
            }}
          />
        </InlineStack>}
      </BlockStack>
      {/* </Card> */}
      <Modal
        open={modal.open}
        onClose={() => {
          if (modal.loading) {
            props.error(t("Kindly wait!"));
            return;
          }
          setModal({ open: false, executeAPI: "", id: "" })
        }}
        title={modal?.title ?? ""}
        primaryAction={{
          content: modal.primaryActionContent,
          destructive: modal.destructive,
          loading: modal.loading,
          onAction: () => { staffAction() }
        }}
        secondaryActions={[
          {
            content: t("Close"),
            disabled: modal.loading,
            onAction: () => { setModal({ open: false, executeAPI: "", id: "" }) }
          }
        ]}
      >
        <Modal.Section>
          {modal.description}
        </Modal.Section>
      </Modal>
    </React.Fragment>
  );
};

export default DI(StaffUserManagement);
